import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    EnvelopeSimpleIcon,
    CircleNotchIcon,
    ArrowLeftIcon,
    CheckCircleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, setSessionToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
const COOLDOWN_SECONDS = 60;

/**
 * Two-step email-OTP sign-in:
 *   step "email" -> enter address  -> POST /auth/email/request-code
 *   step "code"  -> enter 6 digits -> POST /auth/email/verify-code
 *
 * On success calls onSuccess(user) so the parent can navigate.
 * Bilingual labels (中文 / English). data-testid attributes throughout.
 */
export default function EmailSignIn({ onSuccess, onCancel }) {
    const { setUser, refresh } = useAuth();
    const [step, setStep] = useState("email");
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [error, setError] = useState("");
    const codeInputRef = useRef(null);

    useEffect(() => {
        if (step === "code" && codeInputRef.current) {
            codeInputRef.current.focus();
        }
    }, [step]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    const cleanEmail = email.trim().toLowerCase();
    const emailValid = EMAIL_RE.test(cleanEmail);

    const requestCode = async () => {
        setError("");
        if (!emailValid) {
            setError("请输入有效的邮箱地址 / Please enter a valid email.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/email/request-code", { email: cleanEmail });
            setStep("code");
            setCooldown(COOLDOWN_SECONDS);
            toast.success(
                "验证码已发送 / Code sent — check your inbox (and spam).",
            );
        } catch (err) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.detail || "";
            if (status === 429) {
                setError(msg || "请求过于频繁，请稍后再试 / Too many requests. Try again shortly.");
            } else {
                setError(
                    msg ||
                        "发送失败，请稍后再试 / Couldn't send the code. Please try again.",
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const verifyCode = async () => {
        setError("");
        const digits = code.replace(/\D/g, "");
        if (digits.length !== 6) {
            setError("请输入 6 位验证码 / Enter the 6-digit code.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/email/verify-code", {
                email: cleanEmail,
                code: digits,
            });
            if (data?.session_token) setSessionToken(data.session_token);
            if (data?.user) setUser(data.user);
            else await refresh();
            toast.success("登录成功 / Signed in.");
            onSuccess?.(data?.user);
        } catch (err) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.detail || "";
            // Backend already returns user-friendly bilingual-friendly text.
            if (status === 400 || status === 429) {
                setError(
                    msg || "验证码错误 / That code isn't right.",
                );
            } else {
                setError(
                    msg ||
                        "登录失败，请稍后再试 / Sign-in failed. Please try again.",
                );
            }
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        if (cooldown > 0 || busy) return;
        await requestCode();
    };

    const onEmailKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            requestCode();
        }
    };
    const onCodeKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            verifyCode();
        }
    };

    return (
        <div
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
            data-testid="email-signin-card"
        >
            <AnimatePresence mode="wait">
                {step === "email" ? (
                    <motion.div
                        key="step-email"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.25 }}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <EnvelopeSimpleIcon
                                size={18}
                                weight="bold"
                                className="text-white/70"
                            />
                            <h3 className="font-cn font-semibold text-base">
                                邮箱登录 · Email sign-in
                            </h3>
                        </div>
                        <p className="text-xs text-white/50 mb-4">
                            输入邮箱获取 6 位验证码 / We&apos;ll send you a 6-digit code.
                        </p>
                        <input
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            spellCheck={false}
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={onEmailKeyDown}
                            disabled={busy}
                            data-testid="email-input"
                            className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3 text-base outline-none focus:border-white/40 transition-colors"
                        />
                        {error ? (
                            <p
                                data-testid="email-error"
                                className="mt-2 text-xs text-red-400"
                            >
                                {error}
                            </p>
                        ) : null}
                        <div className="mt-4 flex gap-2">
                            {onCancel ? (
                                <button
                                    type="button"
                                    onClick={onCancel}
                                    disabled={busy}
                                    data-testid="email-cancel"
                                    className="rounded-full px-4 py-2.5 text-sm font-medium text-white/70 hover:text-white border border-white/10 hover:border-white/30 transition-colors"
                                >
                                    取消 / Cancel
                                </button>
                            ) : null}
                            <button
                                type="button"
                                onClick={requestCode}
                                disabled={busy}
                                data-testid="send-code-button"
                                className={[
                                    "flex-1 inline-flex items-center justify-center gap-2",
                                    "rounded-full px-4 py-2.5 text-sm font-semibold transition-all",
                                    busy
                                        ? "bg-white/15 text-white/50 cursor-not-allowed"
                                        : "bg-white text-black hover:bg-white/90 active:scale-[0.98]",
                                ].join(" ")}
                            >
                                {busy ? (
                                    <>
                                        <CircleNotchIcon
                                            size={16}
                                            weight="bold"
                                            className="animate-spin"
                                        />
                                        发送中… / Sending…
                                    </>
                                ) : (
                                    <>发送验证码 / Send code</>
                                )}
                            </button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key="step-code"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.25 }}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                if (busy) return;
                                setStep("email");
                                setCode("");
                                setError("");
                            }}
                            data-testid="back-to-email"
                            className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white mb-3 transition-colors"
                        >
                            <ArrowLeftIcon size={12} weight="bold" /> 修改邮箱 / Change email
                        </button>
                        <div className="flex items-center gap-2 mb-1">
                            <CheckCircleIcon
                                size={18}
                                weight="bold"
                                className="text-white/70"
                            />
                            <h3 className="font-cn font-semibold text-base">
                                输入验证码 · Enter your code
                            </h3>
                        </div>
                        <p className="text-xs text-white/55 mb-1">
                            验证码已发送到 / Sent to{" "}
                            <span className="text-white/80">{cleanEmail}</span>
                        </p>
                        <p className="text-[11px] text-white/40 mb-4">
                            10 分钟内有效 / Expires in 10 minutes.
                        </p>
                        <input
                            ref={codeInputRef}
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="6 位数字 / 6 digits"
                            value={code}
                            onChange={(e) =>
                                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                            }
                            onKeyDown={onCodeKeyDown}
                            disabled={busy}
                            maxLength={6}
                            data-testid="code-input"
                            className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-3 text-2xl font-bold tracking-[0.4em] text-center outline-none focus:border-white/40 transition-colors tabular-nums"
                        />
                        {error ? (
                            <p
                                data-testid="code-error"
                                className="mt-2 text-xs text-red-400"
                            >
                                {error}
                            </p>
                        ) : null}
                        <div className="mt-4 flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={verifyCode}
                                disabled={busy || code.length !== 6}
                                data-testid="verify-code-button"
                                className={[
                                    "w-full inline-flex items-center justify-center gap-2",
                                    "rounded-full px-4 py-3 text-sm font-semibold transition-all",
                                    busy || code.length !== 6
                                        ? "bg-white/15 text-white/50 cursor-not-allowed"
                                        : "bg-white text-black hover:bg-white/90 active:scale-[0.98]",
                                ].join(" ")}
                            >
                                {busy ? (
                                    <>
                                        <CircleNotchIcon
                                            size={16}
                                            weight="bold"
                                            className="animate-spin"
                                        />
                                        登录中… / Signing in…
                                    </>
                                ) : (
                                    <>登录 / Sign in</>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={resend}
                                disabled={cooldown > 0 || busy}
                                data-testid="resend-code-button"
                                className={[
                                    "text-xs font-medium transition-colors",
                                    cooldown > 0 || busy
                                        ? "text-white/30 cursor-not-allowed"
                                        : "text-white/70 hover:text-white",
                                ].join(" ")}
                            >
                                {cooldown > 0
                                    ? `重新发送 / Resend in ${cooldown}s`
                                    : "重新发送验证码 / Resend code"}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
